import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Clock, Loader2, MapPin, Sparkles } from 'lucide-react';
import { ProspeccaoJob } from '../../../types';
import { EVENTO_JOB_CRIADO, LABEL_TIPO, cancelarJob, etaTexto, getCotaLemit, getJobsAtivos } from '../../../src/services/prospeccaoFila';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';

// ─────────────────────────────────────────────────────────────────────────────
// Acompanhamento dos lotes da Prospecção.
//
// O trabalho roda no servidor (edge function processar-fila-prospeccao), então
// este banner é só um espelho do estado no banco: pode trocar de aba, sair da
// tela ou fechar o navegador que o job continua. Por isso ele fica acima das
// abas, em ProspeccaoView — para o job seguir visível fora da tela onde nasceu.
// ─────────────────────────────────────────────────────────────────────────────

// 3 s com lote rodando; parado, 30 s — eram 20 consultas por minuto por aba aberta,
// mesmo sem lote nenhum. Lote criado nesta aba acorda o banner na hora (evento abaixo).
const POLL_MS = 3000;
const POLL_PARADO_MS = 30000;

interface Props {
    /** Chamado quando um job termina — a lista recarrega para mostrar o resultado. */
    onConcluir?: () => void;
}

const ICONE = {
    geocode: <MapPin size={15} />,
    lemit_documento: <Sparkles size={15} />,
    lemit_dono: <Sparkles size={15} />,
} as const;

const FilaProspeccaoBanner: React.FC<Props> = ({ onConcluir }) => {
    const { user } = useAuth();
    const [jobs, setJobs] = useState<ProspeccaoJob[]>([]);
    const [cota, setCota] = useState<{ usados: number; limite: number } | null>(null);
    const [cancelando, setCancelando] = useState<string | null>(null);
    // Ids vistos na última rodada: sumiu da lista = terminou.
    const vistosRef = useRef<Set<string>>(new Set());
    const onConcluirRef = useRef(onConcluir);
    onConcluirRef.current = onConcluir;

    useEffect(() => {
        let vivo = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let temAtivo = false;

        const agendar = () => {
            clearTimeout(timer);
            if (vivo) timer = setTimeout(tick, temAtivo ? POLL_MS : POLL_PARADO_MS);
        };

        const tick = async () => {
            // Aba escondida não consulta; volta a consultar quando ela aparecer
            if (document.hidden) { agendar(); return; }
            try {
                const ativos = await getJobsAtivos();
                if (!vivo) return;
                temAtivo = ativos.length > 0;

                const idsAgora = new Set(ativos.map((j) => j.id));
                let terminou = false;
                vistosRef.current.forEach((id) => { if (!idsAgora.has(id)) terminou = true; });
                vistosRef.current = idsAgora;

                setJobs(ativos);
                if (terminou) onConcluirRef.current?.();

                // Cota só interessa quando há job de Lemit em andamento.
                if (ativos.some((j) => j.usa_cota_lemit)) {
                    const c = await getCotaLemit().catch(() => null);
                    if (vivo && c) setCota({ usados: c.usados, limite: c.limite });
                }
            } catch { /* rede instável: tenta de novo no próximo tick */ }
            agendar();
        };

        const agora = () => { clearTimeout(timer); tick(); };
        const aoMostrar = () => { if (!document.hidden) agora(); };
        window.addEventListener(EVENTO_JOB_CRIADO, agora);
        document.addEventListener('visibilitychange', aoMostrar);
        tick();
        return () => {
            vivo = false;
            clearTimeout(timer);
            window.removeEventListener(EVENTO_JOB_CRIADO, agora);
            document.removeEventListener('visibilitychange', aoMostrar);
        };
    }, []);

    const parar = async (job: ProspeccaoJob) => {
        if (!window.confirm(`Parar "${LABEL_TIPO[job.tipo]}"?\n\nO que já foi processado continua salvo; o resto fica sem processar.`)) return;
        setCancelando(job.id);
        try {
            await cancelarJob(job.id);
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'cancelar_job_prospeccao', origem: 'acao' });
            setJobs((prev) => prev.filter((j) => j.id !== job.id));
            vistosRef.current.delete(job.id);
        } finally {
            setCancelando(null);
        }
    };

    if (jobs.length === 0) return null;

    return (
        <div className="space-y-2 px-2 sm:px-0">
            {jobs.map((job) => {
                const pct = job.total ? Math.min(100, (job.processados / job.total) * 100) : 0;
                const pendentes = Math.max(0, job.total - job.processados);
                const erro = job.status === 'erro';
                const pausado = job.status === 'pausado_janela' || job.status === 'pausado_cota';
                const eta = job.status === 'rodando' ? etaTexto(job) : '';

                // Cor: vermelho quando o disjuntor abriu, âmbar quando está só esperando
                // a hora/cota, azul quando está trabalhando.
                const tom = erro
                    ? { caixa: 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/50', texto: 'text-red-700 dark:text-red-300', barra: 'bg-red-500', trilho: 'bg-red-100 dark:bg-red-900/40' }
                    : pausado
                        ? { caixa: 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/50', texto: 'text-amber-700 dark:text-amber-300', barra: 'bg-amber-500', trilho: 'bg-amber-100 dark:bg-amber-900/40' }
                        : { caixa: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800', texto: 'text-sky-700 dark:text-sky-300', barra: 'bg-sky-500', trilho: 'bg-sky-100 dark:bg-sky-900/40' };

                // O horário é do PRÓPRIO job (escolhido no agendamento), não o global.
                const hora = (t: string | null) => (t || '').slice(0, 5);
                const agendado = !!job.janela_inicio;

                const situacao = (() => {
                    if (erro) return 'parado';
                    if (job.status === 'pausado_janela') return `aguardando as ${hora(job.janela_inicio)}`;
                    if (job.status === 'pausado_cota') return agendado ? `retoma às ${hora(job.janela_inicio)}` : 'retoma amanhã';
                    if (job.status === 'pendente' && agendado) return `agendado para as ${hora(job.janela_inicio)}`;
                    if (job.status === 'pendente') return 'na fila';
                    return eta || 'processando';
                })();

                return (
                    <div key={job.id} className={`flex items-start gap-3 p-3 rounded-2xl border ${tom.caixa}`}>
                        <div className={`shrink-0 mt-0.5 ${tom.texto}`}>
                            {erro ? <AlertTriangle size={16} />
                                : pausado || job.status === 'pendente' ? <Clock size={16} />
                                    : <Loader2 size={16} className="animate-spin" />}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-semibold ${tom.texto}`}>
                                <span className="flex items-center gap-1.5">{ICONE[job.tipo]} {LABEL_TIPO[job.tipo]}</span>
                                <span className="opacity-80">
                                    {job.processados.toLocaleString('pt-BR')}/{job.total.toLocaleString('pt-BR')}
                                </span>
                                <span className="opacity-60">· {situacao}</span>
                                {cota && job.usa_cota_lemit && (
                                    <span className="opacity-60">· Lemit hoje: {cota.usados}/{cota.limite}</span>
                                )}
                                {job.max_por_dia != null && (
                                    <span className="opacity-60">· teto do lote: {job.max_por_dia}/dia</span>
                                )}
                                {job.falhas > 0 && (
                                    <span className="opacity-80">· {job.falhas.toLocaleString('pt-BR')} não encontrado(s)</span>
                                )}
                                <button
                                    onClick={() => parar(job)}
                                    disabled={cancelando === job.id}
                                    className="ml-auto hover:underline disabled:opacity-50"
                                >
                                    {cancelando === job.id ? 'Parando…' : erro ? 'Dispensar' : 'Parar'}
                                </button>
                            </div>

                            <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${tom.trilho}`}>
                                <div className={`h-full ${tom.barra} transition-all`} style={{ width: `${pct}%` }} />
                            </div>

                            {erro && job.ultimo_erro && (
                                <p className="mt-1.5 text-[11px] text-red-600/90 dark:text-red-400/90">
                                    {job.ultimo_erro}
                                    <br />
                                    <b>Nenhuma linha foi reprovada</b> — as {pendentes.toLocaleString('pt-BR')} restantes continuam
                                    intactas. Resolva a causa e crie o lote de novo.
                                </p>
                            )}

                            {job.status === 'pausado_cota' && (
                                <p className="mt-1.5 text-[11px] text-amber-700/90 dark:text-amber-400/90">
                                    Faltam {pendentes.toLocaleString('pt-BR')} item(ns).
                                    {job.ultimo_erro ? ` ${job.ultimo_erro}` : ' Continua sozinho quando a cota renovar.'}
                                </p>
                            )}

                            {job.status === 'pendente' && agendado && (
                                <p className="mt-1.5 text-[11px] opacity-70">
                                    Roda das {hora(job.janela_inicio)} às {hora(job.janela_fim)}. Pode fechar o navegador —
                                    quem trabalha é o servidor.
                                </p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FilaProspeccaoBanner;
